/**
 * Token Refresh Service
 *
 * Manages silent token refresh operations without requiring user interaction.
 * Handles refresh state coordination to prevent duplicate concurrent refresh attempts.
 */

import { config } from "@/config";

/**
 * Singleton service for managing token refresh operations
 */
class TokenRefreshService {
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;

  /**
   * Get current refresh state
   * @returns true if a refresh is currently in progress
   */
  getRefreshState(): boolean {
    return this.isRefreshing;
  }

  /**
   * Set the refresh state
   * @param refreshing - whether a refresh is in progress
   */
  setRefreshing(refreshing: boolean): void {
    this.isRefreshing = refreshing;
    if (!refreshing) {
      this.refreshPromise = null;
    }
  }

  /**
   * Refresh the authentication token by calling the refresh endpoint
   * Deduplicates concurrent refresh attempts by returning the same promise
   *
   * @returns Promise that resolves to true if refresh succeeded, false otherwise
   */
  async refreshToken(): Promise<boolean> {
    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start new refresh
    this.setRefreshing(true);

    this.refreshPromise = this.performRefresh();
    return this.refreshPromise;
  }

  /**
   * Perform the actual token refresh operation
   * @private
   */
  private async performRefresh(): Promise<boolean> {
    try {
      const refreshUrl = `${config.auth.serviceUrl}/authn/refresh`;

      const response = await fetch(refreshUrl, {
        method: "GET",
        credentials: "include", // Include HTTP-only cookies
      });

      if (response.ok) {
        this.setRefreshing(false);
        return true;
      }

      // Refresh failed
      this.setRefreshing(false);
      return false;
    } catch {
      // Network error or other failure
      this.setRefreshing(false);
      return false;
    }
  }

  /**
   * Reset the service state (useful for testing)
   */
  reset(): void {
    this.isRefreshing = false;
    this.refreshPromise = null;
  }
}

// Export singleton instance
export const tokenRefreshService = new TokenRefreshService();
